import React, { useMemo, useState } from 'react';
import { Search, ChevronDown } from 'lucide-react';
import type { DerivedMetricSummaryItem, ReferenceRange } from '../../services/nutrition/nutritionProfileService';

/** Pattern for "ideal" / healthy interpretation (matches backend _IDEAL_INTERPRETATION_PATTERN) */
const IDEAL_INTERPRETATION_PATTERN = /normal|healthy|optimal|excellent|good|typical|ideal/i;

/** True if value is within range [min_value, max_value] (inclusive). Mirrors backend _value_in_range. */
function valueInRange(value: number | null, r: ReferenceRange): boolean {
  if (value == null) return false;
  if (r.min_value != null && value < r.min_value) return false;
  if (r.max_value != null && value > r.max_value) return false;
  return true;
}

/** First range with ideal/healthy interpretation. Mirrors backend _find_ideal_range. */
function findIdealRange(ranges: ReferenceRange[]): ReferenceRange | null {
  if (!ranges?.length) return null;
  for (const r of ranges) {
    if (r.interpretation && IDEAL_INTERPRETATION_PATTERN.test(r.interpretation)) return r;
  }
  return null;
}

/** First range that contains value. Mirrors backend _find_matching_range. */
function findMatchingRange(value: number | null, ranges: ReferenceRange[]): ReferenceRange | null {
  if (value == null || !ranges?.length) return null;
  for (const r of ranges) {
    if (valueInRange(value, r)) return r;
  }
  return null;
}

/** Classify value against reference ranges (same logic as backend: ideal range for within_reference, matching range for interpretation_matched). */
function classifyWithReference(
  value: number | null,
  ranges: ReferenceRange[]
): { withinReference: boolean | null; interpretationMatched: string | null } {
  if (value == null || !ranges?.length) {
    return { withinReference: null, interpretationMatched: null };
  }
  const ideal = findIdealRange(ranges);
  const matching = findMatchingRange(value, ranges);
  // Backend: within = _classify_with_reference(value, ideal_row if ideal_row else ref_row)
  const refForWithin = ideal ?? matching ?? ranges[0];
  let withinReference: boolean | null = null;
  if (refForWithin) {
    if (refForWithin.min_value != null && refForWithin.max_value != null) {
      withinReference = refForWithin.min_value <= value && value <= refForWithin.max_value;
    } else if (refForWithin.min_value != null) {
      withinReference = value >= refForWithin.min_value;
    } else if (refForWithin.max_value != null) {
      withinReference = value <= refForWithin.max_value;
    }
  }
  return {
    withinReference,
    interpretationMatched: matching?.interpretation ?? null,
  };
}

/** Map interpretation to segment color for range slider */
function interpretationToColor(interpretation: string | null): string {
  if (!interpretation) return '#e5e7eb';
  const i = interpretation.toLowerCase();
  if (/high|low|poor|critical|risk|elevated|reduced/.test(i)) return '#dc2626';
  if (/moderate|borderline|fair|caution/.test(i)) return '#f59e0b';
  return '#22c55e'; // normal, optimal, healthy, good, typical, ideal
}

export interface RangeSegment {
  min: number;
  max: number;
  color: string;
}

/** Extended slider range: 10% margin on each end so ideal values at the ends lie within the bar. */
const SLIDER_MARGIN_PCT = 0.1;

function extendSliderRange(globalMin: number, globalMax: number): { sliderMin: number; sliderMax: number } {
  const span = globalMax - globalMin;
  const margin = span * SLIDER_MARGIN_PCT;
  return { sliderMin: globalMin - margin, sliderMax: globalMax + margin };
}

export type RangeData = {
  segments: RangeSegment[];
  globalMin: number;
  globalMax: number;
  sliderMin: number;
  sliderMax: number;
};

/** Build ordered segments and global min/max from reference_ranges for multi-color slider. Returns 10% extended slider range. */
function getRangeSegments(item: DerivedMetricSummaryItem): RangeData | null {
  const ranges = item.reference_ranges;
  if (!ranges?.length) return null;

  const actualMins = ranges.map((r) => r.min_value).filter((x): x is number => x != null && Number.isFinite(x));
  const actualMaxs = ranges.map((r) => r.max_value).filter((x): x is number => x != null && Number.isFinite(x));
  const displayMin = actualMins.length > 0 ? Math.min(...actualMins) : null;
  const displayMax = actualMaxs.length > 0 ? Math.max(...actualMaxs) : null;

  if (displayMin == null || displayMax == null || displayMin >= displayMax) return null;

  const rangeSpan = displayMax - displayMin;
  const withBounds = ranges
    .filter((r) => r.min_value != null || r.max_value != null)
    .map((r) => {
      const min = r.min_value != null && Number.isFinite(r.min_value) ? r.min_value : displayMin - rangeSpan * 0.1;
      const max = r.max_value != null && Number.isFinite(r.max_value) ? r.max_value : displayMax + rangeSpan * 0.1;
      return { min, max, color: interpretationToColor(r.interpretation) };
    })
    .sort((a, b) => a.min - b.min);
  if (!withBounds.length) {
    const globalMin = displayMin;
    const globalMax = displayMax;
    const { sliderMin, sliderMax } = extendSliderRange(globalMin, globalMax);
    return { segments: [{ min: globalMin, max: globalMax, color: '#e5e7eb' }], globalMin, globalMax, sliderMin, sliderMax };
  }

  const globalMin = displayMin;
  const globalMax = displayMax;
  const { sliderMin, sliderMax } = extendSliderRange(globalMin, globalMax);
  const segments: RangeSegment[] = [];
  let pos = globalMin;
  for (const s of withBounds) {
    const segStart = Math.max(pos, s.min);
    const segEnd = Math.min(globalMax, s.max);
    if (segEnd > segStart) {
      segments.push({ min: segStart, max: segEnd, color: s.color });
      pos = segEnd;
    }
  }
  if (!segments.length) return { segments: [{ min: globalMin, max: globalMax, color: '#e5e7eb' }], globalMin, globalMax, sliderMin, sliderMax };
  return { segments, globalMin, globalMax, sliderMin, sliderMax };
}

/** True if reference_display describes a 0–100 scale (e.g. "0-100 (points)" or "0–100"). */
function isZeroToHundredDisplay(refDisplay: string | null | undefined): boolean {
  const t = (refDisplay || '').trim();
  return /0\s*[–\-]\s*100|0\s*-\s*100/.test(t) || /\b0-100\b/.test(t);
}

/** Build synthetic range for composite/categorical/temporal metrics from reference_display so we can show a slider. Returns 10% extended slider range. */
function getCompositeOrFallbackRangeData(
  item: DerivedMetricSummaryItem
): RangeData | null {
  const text = (item.reference_display || '').trim();
  const value = item.value != null && Number.isFinite(item.value) ? item.value : null;

  let globalMin: number;
  let globalMax: number;

  // "0-50=Poor, 50-75=Average, 75-100=Good" or "0-100 (points)" or "0-100" or "0–100" (en-dash) – use full scale
  const rangeRegex = /(\d+(?:\.\d+)?)\s*[–\-]\s*(\d+(?:\.\d+)?)/g;
  const pairs: [number, number][] = [];
  let m: RegExpExecArray | null;
  while ((m = rangeRegex.exec(text)) !== null) {
    pairs.push([parseFloat(m[1]), parseFloat(m[2])]);
  }
  if (pairs.length > 0) {
    const mins = pairs.map((p) => p[0]);
    const maxs = pairs.map((p) => p[1]);
    globalMin = Math.min(...mins);
    globalMax = Math.max(...maxs);
    if (globalMin >= globalMax) return null;
  } else if (/negative|younger|positive|older|delta|temporal/i.test(text)) {
    // Biological age delta or similar: -30 to 30
    globalMin = -30;
    globalMax = 30;
  } else if (/0-2=Low|3-4=Moderate|5\+|ordinal/i.test(text) || /\d+=\w+/.test(text)) {
    // "0-2=Low, 3-4=Moderate, 5+=High" or similar – use min/max of all numbers in text
    const nums = text.match(/(\d+(?:\.\d+)?)/g);
    if (nums && nums.length >= 2) {
      const parsed = nums.map((n) => parseFloat(n)).filter(Number.isFinite);
      if (parsed.length >= 2) {
        globalMin = Math.min(...parsed);
        globalMax = Math.max(...parsed);
      } else {
        globalMin = 0;
        globalMax = 10;
      }
    } else {
      globalMin = 0;
      globalMax = 10;
    }
  } else if (text.includes('(') && text.includes(')')) {
    // "Low (1.0) / Moderate (2.0) / High (3.0)" – discrete values only; use min/max of numbers in parentheses
    const nums = text.match(/(\d+(?:\.\d+)?)/g);
    if (nums && nums.length >= 2) {
      const parsed = nums.map((n) => parseFloat(n)).filter(Number.isFinite);
      if (parsed.length >= 2) {
        globalMin = Math.min(...parsed);
        globalMax = Math.max(...parsed);
      } else {
        globalMin = 0;
        globalMax = 100;
      }
    } else {
      globalMin = 0;
      globalMax = 100;
    }
  } else if (value != null) {
    // Heuristic from value: assume 0-100 for scores, or value ±50 for others
    if (value >= 0 && value <= 100) {
      globalMin = 0;
      globalMax = 100;
    } else {
      globalMin = Math.min(value - 50, value * 0.5);
      globalMax = Math.max(value + 50, value * 1.5);
    }
  } else {
    // Default for composite/score type
    globalMin = 0;
    globalMax = 100;
  }

  const segments: RangeSegment[] = [{ min: globalMin, max: globalMax, color: '#e5e7eb' }];
  const { sliderMin, sliderMax } = extendSliderRange(globalMin, globalMax);
  return { segments, globalMin, globalMax, sliderMin, sliderMax };
}

/** Pastel gradient for range scale: green (lower) → light orange → pinkish (higher) */
const RANGE_SCALE_GRADIENT = 'linear-gradient(to right, #dcfce7, #bbf7d0, #ffedd5, #fed7aa, #fce7f3, #fbcfe8)';

/** Indicator color based on whether value is in normal range (green) or not (red) */
function getIndicatorColorForRange(withinReference: boolean | null): string {
  if (withinReference === true) return '#22c55e';
  if (withinReference === false) return '#dc2626';
  return '#9ca3af';
}

/** Slider dot color: use within_reference when set (metrics), else interpretation_matched (scores). For scores, Low/Good→green, Moderate→amber, High/Poor→red. */
function getIndicatorColorForItem(item: DerivedMetricSummaryItem): string {
  if (item.within_reference === true) return '#22c55e';
  if (item.within_reference === false) return '#dc2626';
  const matched = (item as { interpretation_matched?: string | null }).interpretation_matched ?? null;
  return getIndicatorColorFromClassification(item.within_reference, matched);
}

/** Slider dot / status color from effective classification (for document or edited values). */
function getIndicatorColorFromClassification(
  withinReference: boolean | null,
  interpretationMatched: string | null
): string {
  if (withinReference === true) return '#22c55e';
  if (withinReference === false) return '#dc2626';
  if (!interpretationMatched) return '#9ca3af';
  const i = interpretationMatched.toLowerCase();
  if (/high|poor|critical|elevated|risk/.test(i)) return '#dc2626';
  if (/moderate|borderline|fair|caution|average/.test(i)) return '#f59e0b';
  return '#22c55e';
}

/** Round to at most 4 decimal places */
function roundToMax4Decimals(value: number): number {
  return Math.round(value * 10000) / 10000;
}

/** Format number for slider axis labels: max 2 decimal places, trim trailing zeros */
function formatSliderAxisValue(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  if (Number.isInteger(rounded)) return String(rounded);
  const s = rounded.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  return s;
}

/** Format value for display (max 4 decimal places, trim trailing zeros) */
function formatCurrentValue(value: number, unit: string | null): string {
  const rounded = roundToMax4Decimals(value);
  if (Number.isInteger(rounded)) return `${rounded}${unit ? ` ${unit}` : ''}`;
  const s = rounded.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
  return `${s}${unit ? ` ${unit}` : ''}`;
}

/** Get status label for pill from effective classification (so document/edited values use same logic as backend). */
function getStatusLabelFromClassification(
  displayValue: number | null,
  effective: { withinReference: boolean | null; interpretationMatched: string | null },
  referenceRanges: ReferenceRange[]
): string {
  if (displayValue == null) return '—';
  if (effective.interpretationMatched) return effective.interpretationMatched;
  if (effective.withinReference === true) {
    const healthy = referenceRanges?.find(
      (r) => r.interpretation && IDEAL_INTERPRETATION_PATTERN.test(r.interpretation)
    );
    return healthy?.interpretation ?? 'Within range';
  }
  if (effective.withinReference === false) return 'Outside range';
  return '—';
}

/** Get ideal (healthy) range bounds for drawing tick(s) on the bar. Supports single-bound (e.g. ≤ 0.8) or range. */
function getIdealRangeBounds(
  item: DerivedMetricSummaryItem,
  globalMin: number,
  globalMax: number
): { idealMin: number | null; idealMax: number | null } | null {
  const ranges = item.reference_ranges;
  if (!ranges?.length) return null;
  const healthy = ranges.find(
    (r) =>
      r.interpretation &&
      /normal|healthy|optimal|excellent|good|typical/i.test(r.interpretation)
  );
  if (!healthy) return null;
  const minV = healthy.min_value != null && Number.isFinite(healthy.min_value) ? healthy.min_value : null;
  const maxV = healthy.max_value != null && Number.isFinite(healthy.max_value) ? healthy.max_value : null;
  if (minV == null && maxV == null) return null;
  if (minV != null && maxV != null && minV >= maxV) return null;
  return { idealMin: minV ?? null, idealMax: maxV ?? null };
}

/** Format range text for display */
function formatRangeText(item: DerivedMetricSummaryItem): string {
  const ranges = item.reference_ranges;
  if (!ranges?.length) return item.reference_display || 'N/A';
  const healthy = ranges.find(
    (r) =>
      r.interpretation &&
      /normal|healthy|optimal|excellent|good|typical/i.test(r.interpretation)
  );
  if (!healthy) return item.reference_display || 'N/A';
  const u = item.reference_unit || item.unit || '';
  if (healthy.min_value != null && healthy.max_value != null)
    return `${healthy.min_value} - ${healthy.max_value} ${u}`.trim();
  if (healthy.min_value != null) return `≥ ${healthy.min_value} ${u}`.trim();
  if (healthy.max_value != null) return `≤ ${healthy.max_value} ${u}`.trim();
  return item.reference_display || 'N/A';
}

/** Format recency_date or date as "today", "1 day ago", "3 days ago", "1 week ago", "2 weeks ago", "1 month ago", etc. */
function formatRecency(recencyDate: string | null, fallbackDate: string | null): string | null {
  const raw = recencyDate ?? fallbackDate;
  if (!raw || typeof raw !== 'string') return null;
  let d: Date;
  try {
    d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
  } catch {
    return null;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  const diffMs = today.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (diffDays < 0) return null;
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 14) return '1 week ago';
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 60) return '1 month ago';
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) > 1 ? 's' : ''} ago`;
}

export interface HealthVitalsInsightsViewProps {
  /** Metrics or scores list (already filtered by sub-tab) */
  items: DerivedMetricSummaryItem[];
  loading?: boolean;
  error?: string | null;
  /** Sub-tab: 'metrics' | 'scores' */
  insightsSubTab: 'metrics' | 'scores';
  onInsightsSubTabChange: (tab: 'metrics' | 'scores') => void;
  /** Edit mode (patient detail page) */
  editMode?: boolean;
  onEditModeChange?: (value: boolean) => void;
  /** Current edits: metric_key -> number */
  edits: Record<string, number>;
  onEditsChange: (next: Record<string, number>) => void;
  /** Save handler (patient detail); when absent, no Save/Cancel bar */
  onSave?: () => void;
  onCancel?: () => void;
  saving?: boolean;
  /** e.g. "Save 3 metrics" */
  saveButtonLabel?: string;
  message?: string | null;
  messageLevel?: 'info' | 'success' | 'error';
  /** When false, the save/error message box is never shown (e.g. patient profile Insights tab). */
  showMessageBox?: boolean;
  /** For nutrition profile: get display value from parsed document when present */
  getDisplayValue?: (item: DerivedMetricSummaryItem) => number | null;
  /** For nutrition profile: show source column and legend */
  showSourceColumn?: boolean;
  /** Refetch (nutrition profile Refresh button) */
  onRefresh?: () => void;
  refreshLoading?: boolean;
  /** Profile just created note (nutrition profile) */
  profileJustCreated?: boolean;
}

export function HealthVitalsInsightsView({
  items,
  loading = false,
  error = null,
  insightsSubTab,
  onInsightsSubTabChange,
  editMode = false,
  onEditModeChange,
  edits,
  onEditsChange,
  onSave,
  onCancel,
  saving = false,
  saveButtonLabel,
  message,
  messageLevel = 'info',
  showMessageBox = true,
  getDisplayValue,
  showSourceColumn = false,
  onRefresh,
  refreshLoading = false,
  profileJustCreated = false,
}: HealthVitalsInsightsViewProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [showAllRows] = useState(true);
  const [openDescriptionKey, setOpenDescriptionKey] = useState<string | null>(null);

  const categories = useMemo(() => {
    const set = new Set<string>();
    items.forEach((i) => {
      const c = i.category?.trim() || 'Other';
      set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filteredAndGrouped = useMemo(() => {
    let list = items;
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(
        (i) =>
          i.display_name?.toLowerCase().includes(q) ||
          i.metric_key?.toLowerCase().includes(q)
      );
    }
    if (categoryFilter) {
      list = list.filter((i) => (i.category?.trim() || 'Other') === categoryFilter);
    }
    const byCategory: Record<string, DerivedMetricSummaryItem[]> = {};
    list.forEach((item) => {
      const cat = item.category?.trim() || 'Other';
      if (!byCategory[cat]) byCategory[cat] = [];
      byCategory[cat].push(item);
    });
    categories.forEach((c) => {
      if (byCategory[c]) byCategory[c].sort((a, b) => a.metric_key.localeCompare(b.metric_key));
    });
    return byCategory;
  }, [items, searchQuery, categoryFilter, categories]);

  const flatFiltered = useMemo(
    () => Object.values(filteredAndGrouped).flat(),
    [filteredAndGrouped]
  );

  const activeCount = useMemo(() => {
    return items.filter((i) => {
      const value = edits[i.metric_key] !== undefined && Number.isFinite(edits[i.metric_key])
        ? edits[i.metric_key]
        : getDisplayValue?.(i) ?? (i.value != null && typeof i.value === 'number' ? i.value : null);
      const { withinReference } = classifyWithReference(value, i.reference_ranges ?? []);
      return value != null && withinReference === true;
    }).length;
  }, [items, edits, getDisplayValue]);

  const alertsCount = useMemo(() => {
    return items.filter((i) => {
      const value = edits[i.metric_key] !== undefined && Number.isFinite(edits[i.metric_key])
        ? edits[i.metric_key]
        : getDisplayValue?.(i) ?? (i.value != null && typeof i.value === 'number' ? i.value : null);
      const { withinReference } = classifyWithReference(value, i.reference_ranges ?? []);
      return withinReference === false;
    }).length;
  }, [items, edits, getDisplayValue]);

  const visibleKeys = useMemo(
    () => new Set(flatFiltered.map((i) => i.metric_key)),
    [flatFiltered]
  );

  const getValue = (item: DerivedMetricSummaryItem): number | null => {
    const edited = edits[item.metric_key];
    if (edited !== undefined && Number.isFinite(edited)) return edited;
    if (getDisplayValue) {
      const fromParsed = getDisplayValue(item);
      if (fromParsed != null && Number.isFinite(fromParsed)) return fromParsed;
    }
    return item.value != null && typeof item.value === 'number' ? item.value : null;
  };

  /** Display source: same mapping for metrics and scores. derived/aggregated -> calculated; doctor_portal/doctor_updated/manual_entry/document_extraction -> device/manual. */
  const getSourceLabel = (item: DerivedMetricSummaryItem): string => {
    if (!showSourceColumn) return '—';
    const edited = edits[item.metric_key];
    if (edited !== undefined) return 'Edited';
    const raw = (item.source ?? item.vital_data_source ?? '').toString().trim().toLowerCase();
    if (!raw) return '—';
    if (raw === 'derived' || raw === 'aggregated') return 'calculated';
    if (raw === 'doctor_portal' || raw === 'doctor_updated' || raw === 'manual_entry' || raw === 'document_extraction') return 'device/manual';
    return (item.source ?? item.vital_data_source ?? '').toString().trim();
  };

  /** Source type for styling value input and status (green = from document, yellow = edited, neutral = calculated/device/manual). */
  const getSourceType = (item: DerivedMetricSummaryItem): 'from_document' | 'edited' | 'user_data' => {
    const edited = edits[item.metric_key];
    if (edited !== undefined) return 'edited';
    if (getDisplayValue && getDisplayValue(item) != null) return 'from_document';
    const src = (item.source || '').toLowerCase();
    const vds = (item.vital_data_source || '').toLowerCase();
    if (src === 'document_extraction' || vds === 'document_extraction') return 'from_document';
    return 'user_data';
  };

  const isComposite = (item: DerivedMetricSummaryItem) =>
    item.category === 'Composite Score' || (item.category?.toLowerCase() ?? '').includes('composite');

  if (loading) {
    return (
      <div style={{ color: '#6b7280', fontSize: '0.95rem', padding: '20px' }}>
        Loading insights…
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message" style={{ marginTop: '8px', padding: '20px' }}>
        {error}
      </div>
    );
  }

  return (
    <div className="health-vitals-insights" style={{ background: '#ffffff', minHeight: '100%' }}>
      {profileJustCreated && (
        <div
          style={{
            marginBottom: '16px',
            padding: '12px 16px',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '0.9rem',
            color: '#1e40af',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}
        >
          <strong>Note:</strong> Insights may take 2–5 minutes to load after creating a profile. We
          aggregate data in the background. Use <strong>Refresh</strong> below to check again.
        </div>
      )}

      {/* Insights title + Metrics/Scores tabs + Active metrics / Alerts cards */}
      <div style={{ marginBottom: '20px' }}>
        <h2 className="section-title" style={{ marginBottom: '12px' }}>
          Insights
        </h2>
        <div className="patient-tabs" style={{ marginBottom: '16px' }}>
          <button
            type="button"
            className={`patient-tab ${insightsSubTab === 'metrics' ? 'active' : ''}`}
            onClick={() => onInsightsSubTabChange('metrics')}
            style={{ fontSize: '1.0625rem', fontWeight: 600 }}
          >
            Metrics
          </button>
          <button
            type="button"
            className={`patient-tab ${insightsSubTab === 'scores' ? 'active' : ''}`}
            onClick={() => onInsightsSubTabChange('scores')}
            style={{ fontSize: '1.0625rem', fontWeight: 600 }}
          >
            Scores
          </button>
        </div>
        <div
          className="health-vitals-summary-cards"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '16px',
          }}
        >
          <div
            style={{
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}
          >
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', letterSpacing: '0.05em', marginBottom: '8px' }}>
              ACTIVE METRICS
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>{activeCount}</span>
              <span
                style={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#ffffff',
                  background: '#15803d',
                  padding: '4px 10px',
                  borderRadius: '9999px',
                }}
              >
                HEALTHY
              </span>
            </div>
          </div>
          <div
            style={{
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}
          >
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', letterSpacing: '0.05em', marginBottom: '8px' }}>
              ALERTS
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>{alertsCount}</span>
              <span
                style={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#ffffff',
                  background: '#ea580c',
                  padding: '4px 10px',
                  borderRadius: '9999px',
                }}
              >
                ACTION
              </span>
            </div>
          </div>
        </div>
        {onRefresh && (
          <div style={{ marginTop: '12px' }}>
            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshLoading}
              style={{
                padding: '8px 14px',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#374151',
                background: refreshLoading ? '#e5e7eb' : '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: refreshLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {refreshLoading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        )}
        {showMessageBox && message && editMode && (
          <div
            style={{
              marginTop: '8px',
              color: messageLevel === 'error' ? '#dc2626' : messageLevel === 'success' ? '#15803d' : '#1d4ed8',
              fontSize: '0.85rem',
            }}
          >
            {message}
          </div>
        )}
      </div>

      {items.length === 0 && (
        <div style={{ color: '#6b7280', fontSize: '0.95rem', padding: '20px' }}>
          No insights are available yet for this patient.
        </div>
      )}

      {items.length > 0 && (
        <>
          {/* Health Vitals Index: centered title; row below: Edit/Save/Cancel + count + filter + search */}
          <div className="health-vitals-index-header" style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', minWidth: '160px' }}>
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    title="Filter by category"
                    style={{
                      width: '100%',
                      padding: '8px 36px 8px 14px',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      background: '#f9fafb',
                      color: '#374151',
                      cursor: 'pointer',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      minHeight: '40px',
                      boxSizing: 'border-box',
                    }}
                  >
                    <option value="">All categories</option>
                    {categories.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#6b7280',
                      opacity: 0.8,
                      pointerEvents: 'none',
                    }}
                    aria-hidden
                  />
                </div>
                <div style={{ position: 'relative' }}>
                  <Search
                    size={18}
                    style={{
                      position: 'absolute',
                      left: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#9ca3af',
                      pointerEvents: 'none',
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Search metrics..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      padding: '8px 12px 8px 36px',
                      fontSize: '0.875rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      minWidth: '180px',
                    }}
                  />
                </div>
              </div>
              {onEditModeChange && (
                <>
                  {!editMode ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                      <button
                        type="button"
                        onClick={() => onEditModeChange(true)}
                        disabled={items.length === 0}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: items.length === 0 ? '#e5e7eb' : '#2563eb',
                          color: items.length === 0 ? '#9ca3af' : '#ffffff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: items.length === 0 ? 'not-allowed' : 'pointer',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                        }}
                      >
                        Edit metrics
                      </button>
                      <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
                        {items.length} metric{items.length === 1 ? '' : 's'} available
                      </span>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {onSave && (
                        <button
                          type="button"
                          onClick={onSave}
                          disabled={saving}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: saving ? '#93c5fd' : '#2563eb',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: saving ? 'not-allowed' : 'pointer',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                          }}
                        >
                          {saving ? 'Saving…' : saveButtonLabel ?? 'Save'}
                        </button>
                      )}
                      {onCancel && (
                        <button
                          type="button"
                          onClick={onCancel}
                          disabled={saving}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#f3f4f6',
                            color: '#111827',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            cursor: saving ? 'not-allowed' : 'pointer',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                          }}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div
            style={{
              marginTop: '8px',
              marginBottom: '24px',
              padding: '20px',
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}
          >
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: '#ffffff', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '12px 4px 12px 8px', textAlign: 'left', fontWeight: 600, color: '#b45309', fontSize: '0.75rem', letterSpacing: '0.05em', width: '200px' }}>
                      BIOMARKER
                    </th>
                    <th style={{ padding: '12px 4px 12px 4px', textAlign: 'left', fontWeight: 600, color: '#b45309', fontSize: '0.75rem', letterSpacing: '0.05em', width: '120px' }}>
                      STATUS
                    </th>
                    {showSourceColumn && (
                      <th style={{ padding: '12px 12px 12px 4px', textAlign: 'left', fontWeight: 600, color: '#b45309', fontSize: '0.75rem', letterSpacing: '0.05em', width: '100px' }}>
                        SOURCE
                      </th>
                    )}
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#b45309', fontSize: '0.75rem', letterSpacing: '0.05em', width: '220px', maxWidth: '220px' }}>
                      RANGE ANALYSIS
                    </th>
                    <th style={{ padding: '12px 16px 12px 24px', textAlign: 'center', fontWeight: 600, color: '#b45309', fontSize: '0.75rem', letterSpacing: '0.05em', width: '100px' }}>
                      VALUE
                    </th>
                    <th style={{ padding: '12px 16px', width: '44px', fontWeight: 600, color: '#b45309', fontSize: '0.75rem', letterSpacing: '0.05em' }} />
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(filteredAndGrouped).map(([category, categoryItems]) => {
                    const firstVisibleIndex = categoryItems.findIndex((i) => showAllRows || visibleKeys.has(i.metric_key));
                    const hasVisibleInCategory = categoryItems.some((i) => showAllRows || visibleKeys.has(i.metric_key));
                    return (
                      <React.Fragment key={category}>
                        {hasVisibleInCategory && (
                          <tr style={{ backgroundColor: '#fef3e2' }}>
                            <td
                              colSpan={showSourceColumn ? 6 : 5}
                              style={{
                                padding: '12px 16px 8px 8px',
                                borderBottom: '1px solid #e5e7eb',
                                textAlign: 'center',
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                letterSpacing: '0.04em',
                                color: '#b45309',
                              }}
                            >
                              {category.toUpperCase()}
                            </td>
                          </tr>
                        )}
                        {categoryItems.map((item, idx) => {
                      const isVisible = showAllRows || visibleKeys.has(item.metric_key);
                      if (!isVisible) return null;
                      const isFirstInCategory = idx === firstVisibleIndex;
                      const displayValue = getValue(item);
                      const effectiveClassification = classifyWithReference(displayValue, item.reference_ranges ?? []);
                      const statusLabel = getStatusLabelFromClassification(
                        displayValue,
                        effectiveClassification,
                        item.reference_ranges ?? []
                      );
                      const rangeData = (() => {
                        // For composite scores with "0-100" ideal (e.g. Body Score), use reference_display so slider matches "Ideal: 0-100"
                        if (isComposite(item) && isZeroToHundredDisplay(item.reference_display)) {
                          const fromDisplay = getCompositeOrFallbackRangeData(item);
                          if (fromDisplay) return fromDisplay;
                        }
                        return getRangeSegments(item) ?? (isComposite(item) ? getCompositeOrFallbackRangeData(item) : null);
                      })();
                      const idealDisplay =
                        item.reference_display?.trim() ?
                          [item.reference_display.trim(), item.reference_unit || item.unit].filter(Boolean).join(' ') : null;
                      const rangeText = isComposite(item)
                        ? (item.reference_display || item.unit || 'N/A')
                        : (idealDisplay || formatRangeText(item));
                      const isAlert = effectiveClassification.withinReference === false;
                      const hasStatus = displayValue != null && statusLabel !== '—';
                      const statusColor = !hasStatus ? '#9ca3af' : isAlert ? '#dc2626' : '#15803d';
                      const statusBg = !hasStatus ? '#f3f4f6' : isAlert ? '#fef2f2' : '#dcfce7';
                      const indicatorColor = getIndicatorColorFromClassification(
                        effectiveClassification.withinReference,
                        effectiveClassification.interpretationMatched
                      );
                      const isDescOpen = openDescriptionKey === item.metric_key;

                      return (
                        <React.Fragment key={item.metric_key}>
                        <tr
                          style={{
                            borderBottom: '1px solid #e5e7eb',
                            backgroundColor: '#ffffff',
                          }}
                        >
                          <td style={{ padding: '14px 4px 14px 8px', verticalAlign: 'top' }}>
                            <div style={{ lineHeight: 1.4 }}>
                              <span style={{ fontWeight: 700, color: '#111827', fontSize: '0.875rem', wordWrap: 'break-word' }}>
                                {item.display_name}
                              </span>
                              {(formatRecency(item.recency_date ?? null, item.date ?? null)) && (
                                <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: 2 }}>
                                  since {formatRecency(item.recency_date ?? null, item.date ?? null)}
                                </div>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '14px 4px 14px 4px', verticalAlign: 'top' }}>
                            {showSourceColumn && getSourceType(item) === 'from_document' && !isAlert ? (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  color: '#166534',
                                  background: '#dcfce7',
                                  border: '1px solid #22c55e',
                                  padding: '4px 10px',
                                  borderRadius: '9999px',
                                }}
                              >
                                {statusLabel}
                              </span>
                            ) : (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  color: statusColor,
                                  background: statusBg,
                                  padding: '4px 10px',
                                  borderRadius: '9999px',
                                }}
                              >
                                {statusLabel}
                              </span>
                            )}
                          </td>
                          {showSourceColumn && (
                            <td style={{ padding: '14px 12px 14px 4px', fontSize: '0.8125rem', color: '#6b7280', verticalAlign: 'top' }}>
                              {getSourceLabel(item)}
                            </td>
                          )}
                          <td style={{ padding: '14px 24px 14px 8px', verticalAlign: 'top', width: '220px', maxWidth: '220px', textAlign: 'center' }}>
                            {rangeData ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', width: '100%', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                  <div
                                    style={{
                                      position: 'relative',
                                      flex: 1,
                                      minWidth: 0,
                                      height: '8px',
                                      borderRadius: '4px',
                                      overflow: 'visible',
                                      background: RANGE_SCALE_GRADIENT,
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    {(() => {
                                      const idealBounds = getIdealRangeBounds(item, rangeData.globalMin, rangeData.globalMax);
                                      const { sliderMin, sliderMax } = rangeData;
                                      const span = sliderMax - sliderMin;
                                      const toPct = (v: number) =>
                                        span <= 0 ? 0 : Math.min(100, Math.max(0, ((v - sliderMin) / span) * 100));
                                      const tickStyle = {
                                        position: 'absolute' as const,
                                        top: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: '2px',
                                        height: '12px',
                                        borderRadius: '1px',
                                        background: '#374151',
                                        opacity: 0.8,
                                        zIndex: 1,
                                      };
                                      const clampedValue =
                                        displayValue != null
                                          ? Math.max(sliderMin, Math.min(sliderMax, displayValue))
                                          : null;
                                      return (
                                        <>
                                          {idealBounds && (
                                            <>
                                              {idealBounds.idealMin != null && (
                                                <div style={{ ...tickStyle, left: `${toPct(idealBounds.idealMin)}%` }} />
                                              )}
                                              {idealBounds.idealMax != null && (idealBounds.idealMin == null || idealBounds.idealMin !== idealBounds.idealMax) && (
                                                <div style={{ ...tickStyle, left: `${toPct(idealBounds.idealMax)}%` }} />
                                              )}
                                            </>
                                          )}
                                          {clampedValue != null && (
                                            <div
                                              style={{
                                                position: 'absolute',
                                                left: `${toPct(clampedValue)}%`,
                                                top: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                width: '5px',
                                                height: '10px',
                                                borderRadius: '1px',
                                                background: indicatorColor,
                                                border: '1px solid rgba(0,0,0,0.12)',
                                                boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                                                zIndex: 2,
                                              }}
                                            />
                                          )}
                                        </>
                                      );
                                    })()}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '100%',
                                    fontSize: '0.75rem',
                                    color: '#6b7280',
                                    flexWrap: 'wrap',
                                    gap: '4px',
                                  }}
                                >
                                  <span>{formatSliderAxisValue(rangeData.sliderMin)}</span>
                                  {rangeText ? (
                                    <span style={{ flex: 1, textAlign: 'center', color: '#374151', fontWeight: 500 }}>
                                      Ideal: {rangeText}
                                    </span>
                                  ) : (
                                    <span style={{ flex: 1 }} />
                                  )}
                                  <span>{formatSliderAxisValue(rangeData.sliderMax)}</span>
                                </div>
                              </div>
                            ) : (
                              <span style={{ fontSize: '0.875rem', color: '#4b5563' }}>{rangeText ? `Ideal: ${rangeText}` : '—'}</span>
                            )}
                          </td>
                          <td style={{ padding: '14px 16px 14px 24px', verticalAlign: 'top', textAlign: 'center', width: '100px', maxWidth: '100px' }}>
                            {editMode ? (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <input
                                  type="number"
                                  step="any"
                                  value={displayValue != null ? roundToMax4Decimals(displayValue) : ''}
                                  onChange={(e) => {
                                    const raw = e.target.value.trim();
                                    if (raw === '') {
                                      const next = { ...edits };
                                      delete next[item.metric_key];
                                      onEditsChange(next);
                                      return;
                                    }
                                    const num = parseFloat(raw);
                                    if (!Number.isNaN(num)) onEditsChange({ ...edits, [item.metric_key]: roundToMax4Decimals(num) });
                                  }}
                                  placeholder="-"
                                  style={(() => {
                                    const source = showSourceColumn ? getSourceType(item) : 'user_data';
                                    const base = {
                                      minWidth: '80px',
                                      padding: '6px 10px',
                                      fontSize: '0.875rem',
                                      borderRadius: '4px',
                                      fontWeight: 700,
                                      textAlign: 'center' as const,
                                    };
                                    if (source === 'from_document') {
                                      return { ...base, border: '1px solid #22c55e', backgroundColor: '#dcfce7' };
                                    }
                                    if (source === 'edited') {
                                      return { ...base, border: '1px solid #eab308', backgroundColor: '#fef9c3' };
                                    }
                                    return { ...base, border: '1px solid #d1d5db', backgroundColor: '#ffffff' };
                                  })()}
                                />
                                {item.unit && item.unit.toLowerCase() !== 'unknown' && <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>{item.unit}</span>}
                                {edits[item.metric_key] !== undefined && (
                                  <button
                                    type="button"
                                    onClick={() => { const next = { ...edits }; delete next[item.metric_key]; onEditsChange(next); }}
                                    style={{ padding: '4px 8px', fontSize: '0.75rem', cursor: 'pointer', color: '#374151', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px' }}
                                    title="Revert to value from document"
                                  >
                                    Revert
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span style={{ fontWeight: 700, color: '#111827', fontSize: '0.875rem' }}>
                                {displayValue != null && typeof displayValue === 'number'
                                  ? formatCurrentValue(displayValue, item.unit && item.unit.toLowerCase() !== 'unknown' ? item.unit : null)
                                  : '—'}
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '14px 16px', verticalAlign: 'top', width: '44px', textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={() => setOpenDescriptionKey(isDescOpen ? null : item.metric_key)}
                              style={{
                                padding: '4px',
                                border: 'none',
                                background: 'transparent',
                                color: '#9ca3af',
                                cursor: 'pointer',
                                display: 'inline-flex',
                              }}
                              title="Show row description"
                            >
                              <ChevronDown
                                size={20}
                                style={{ transform: isDescOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                              />
                            </button>
                          </td>
                        </tr>
                        {isDescOpen && item.description && (
                          <tr style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
                            <td
                              colSpan={showSourceColumn ? 6 : 5}
                              style={{
                                padding: '12px 16px 16px',
                                verticalAlign: 'top',
                                borderTop: 'none',
                                background: '#f9fafb',
                                fontSize: '0.8125rem',
                                color: '#374151',
                                lineHeight: 1.5,
                              }}
                            >
                              {item.description}
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      );
                    })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

          </div>
        </>
      )}
    </div>
  );
}
